import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { LanguageToggle } from '@/components/LanguageToggle';
import { ThemeToggle } from '@/components/ThemeToggle';
import { ApiError } from '@/lib/api';
import { useLogin, useSecondFactor } from '@/lib/queries';

/**
 * Вход в два шага: пароль, затем код. Второй фактор обязателен, потому что
 * приложение доступно из интернета (docs/SECURITY.md, раздел 5).
 */
export function Login({ secondFactorPending }: { secondFactorPending: boolean }) {
  const { t } = useTranslation();
  const login = useLogin();
  const secondFactor = useSecondFactor();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  const stage = secondFactorPending || login.isSuccess ? 'code' : 'password';
  const error = stage === 'code' ? secondFactor.error : login.error;

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 px-4 pt-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))]">
      <div className="w-full max-w-sm">
        <h1 className="font-serif text-3xl font-semibold">{t('app.name')}</h1>
        <p className="mt-1 text-sm text-muted">{t('app.tagline')}</p>

        {stage === 'password' ? (
          <form
            className="mt-8 flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              login.mutate({ email: email.trim(), password });
            }}
          >
            <label className="flex flex-col gap-1 text-sm">
              {t('auth.email')}
              <input
                type="email"
                autoComplete="username"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="min-h-11 rounded-lg border border-line bg-surface px-3 outline-none focus:border-accent"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              {t('auth.password')}
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="min-h-11 rounded-lg border border-line bg-surface px-3 outline-none focus:border-accent"
              />
            </label>
            <Button
              type="submit"
              variant="primary"
              disabled={!email.trim() || !password || login.isPending}
            >
              {t('auth.signIn')}
            </Button>
          </form>
        ) : (
          <form
            className="mt-8 flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              secondFactor.mutate(code.trim());
            }}
          >
            <p className="text-sm text-ink-2">{t('auth.codeHint')}</p>
            <label className="flex flex-col gap-1 text-sm">
              {t('auth.code')}
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="min-h-11 rounded-lg border border-line bg-surface px-3 font-mono tracking-widest outline-none focus:border-accent"
              />
            </label>
            <Button
              type="submit"
              variant="primary"
              disabled={!code.trim() || secondFactor.isPending}
            >
              {t('auth.confirm')}
            </Button>
            <p className="text-xs text-muted">{t('auth.recoveryHint')}</p>
          </form>
        )}

        {error && (
          <p role="alert" className="mt-4 text-sm text-danger">
            {messageFor(error, t)}
          </p>
        )}
      </div>

      <div className="flex gap-2">
        <LanguageToggle />
        <ThemeToggle />
      </div>
    </div>
  );
}

function messageFor(error: unknown, t: (key: string) => string): string {
  if (!(error instanceof ApiError)) return t('errors.loadFailed');
  switch (error.code) {
    case 'invalid_credentials':
      return t('auth.error.credentials');
    case 'invalid_code':
      return t('auth.error.code');
    case 'too_many_attempts':
      return t('auth.error.throttled');
    case 'offline':
      return t('errors.loadFailed');
    default:
      return t('auth.error.generic');
  }
}
